<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <FormRoute
          :data="route"
          :loading="isFormLoading"
          :disabled="isMutationLoading"
          :dictionaries="dictionaries"
          :cities="cities"
          :events="events"
          :language="language"
          :isNew="!id"
          @submit="routeSubmitHandle"
          @cancel="$router.push({ name: 'page-routes' })"
          @delete-route="deleteRoute"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmRouteRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import ConfirmationDialog from '@/components/confirmation-dialog'
import FormRoute from '@/pages/route/form/FormRoute.vue'
import routeQuery from '@/services/gql/types/Routes/queries/route.gql'
import updateRouteMutation from '@/services/gql/types/Routes/mutations/updateRoute.gql'
import createRouteMutation from '@/services/gql/types/Routes/mutations/createRoute.gql'
import deleteRouteMutation from '@/services/gql/types/Routes/mutations/deleteRoute.gql'
import updateRoutePointMutation from '@/services/gql/types/RoutePoints/mutations/updateRoutePoint.gql'
import createRoutePointMutation from '@/services/gql/types/RoutePoints/mutations/createRoutePoint.gql'
import deleteRoutePointMutation from '@/services/gql/types/RoutePoints/mutations/deleteRoutePoint.gql'
import dictionariesQuery from '@/services/gql/types/Dictionaries/queries/dictionaries.gql'
import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import eventsQuery from '@/services/gql/types/Events/queries/eventsGrid.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

export default {
  name: 'PageRoute',
  props: {
    id: {
      type: [Number, String],
      default: null
    }
  },
  components: {
    FormRoute,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать маршрут' : 'Создать маршрут'
    }
  },
  computed: {
    isFormLoading () {
      return (
        this.$apollo.queries.route.loading ||
        this.$apollo.queries.dictionaries.loading ||
        this.$apollo.queries.cities.loading ||
        this.$apollo.queries.events.loading
      )
    },
    isMutationLoading () {
      return this.$async.createRoute.$pending || this.$async.updateRoute.$pending
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      route: {},
      dictionaries: [],
      cities: null,
      events: null
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async refetchRoute () {
      return await this.$apollo.queries.route.refetch()
    },
    async deleteRoute () {
      try {
        await this.$refs.confirmRouteRemoving.open()
        await this.$async.deleteRoute.$perform({ id: this.id })
        this.$successMsg('Маршрут удален')
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении маршрута с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-routes' })
      }
    },
    routeSubmitHandle (routePayload) {
      if (this.id) this.updateRoute(routePayload)
      else this.createRoute(routePayload)
    },
    async createRoutePoint (routeId, pointData) {
      delete pointData.id
      await this.$async.createRoutePoint.$perform({
        data: {
          ...pointData,
          routeId
        }
      })
    },
    async createRoute (routePayload) {
      try {
        delete routePayload.data.translations
        let points = routePayload.data.points
        delete routePayload.data.points
        const {
          data: { createRoute }
        } = await this.$async.createRoute.$perform({
          data: routePayload.data
        })
        points = points.map((point, pointIndex) => {
          point.data.sortOrder = pointIndex
          return point
        })
        for (const point of points) {
          if (createRoute.id) {
            if (createRoute.id) {
              await this.changeRoutePoint(createRoute.id, point.data)
            }
          }
        }
        await this.refetchRoute()
        this.$successMsg('Маршрут создан')
        this.$emit('updated')
        await this.$router.push({ name: 'page-routes' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании маршрута')
      }
    },
    async changeRoutePoint (routeId, pointData) {
      if (pointData.id) {
        return this.updateRoutePoint(routeId, pointData)
      } else {
        return this.createRoutePoint(routeId, pointData)
      }
    },
    async removeRoutePoint (id) {
      try {
        return this.$async.deleteRoutePoint.$perform({ id })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при удалении точки')
      }
    },
    async updateRoutePoint (routeId, pointData) {
      const id = pointData.id
      delete pointData.id
      pointData.translations = {}
      setTranslation(pointData, this.language)
      await this.$async.updateRoutePoint.$perform(
        {
          id,
          data: {
            ...pointData,
            routeId
          }
        },
        { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
      )
    },
    async updateRoute (routePayload) {
      const data = routePayload.data
      const routeId = Number(this.id)
      const points = [...data.points]
      const sortPoints = points.map((point, pointIndex) => {
        point.data.sortOrder = pointIndex
        return point
      })
      delete data.points
      const promises = []
      try {
        setTranslation(data, this.language)
        // update route
        promises.push(
          this.$async.updateRoute.$perform(
            {
              id: routeId,
              data: data
            },
            {
              Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
            }
          )
        )
        // update points
        for (const point of sortPoints) {
          if (routeId) {
            promises.push(this.changeRoutePoint(routeId, point.data))
          }
        }
        // delete points
        for (const pointToRemove of routePayload.postActions.itemsToRemove) {
          if (pointToRemove) {
            promises.push(this.removeRoutePoint(pointToRemove))
          }
        }
        promises.push(this.refetchRoute())
        await Promise.all(promises)
        this.$successMsg('Маршрут обновлен')
        this.$emit('updated')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении маршрута')
      }
    }
  },
  apollo: {
    route: {
      fetchPolicy: 'network-only',
      query: routeQuery,
      manual: true,
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-routes' })
        }
        this.route = res?.data?.route
      }
    },
    dictionaries: {
      fetchPolicy: 'network-only',
      query: dictionariesQuery,
      variables () {
        return { where: {} }
      },
      result ({ data }) {
        this.dictionaries = data.dictionaries.list
      }
    },
    cities: {
      fetchPolicy: 'network-only',
      query: citiesQuery,
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result (res) {
        this.cities = res?.data?.cities?.list?.map(city => ({
          id: city.id,
          title: city.title
        }))
      }
    },
    events: {
      fetchPolicy: 'network-only',
      query: eventsQuery,
      variables () {
        return {
          where: {}
        }
      },
      result (res) {
        this.events = res?.data?.events?.list || null
      }
    }
  },
  async: {
    deleteRoute (variables) {
      return this.$apollo.mutate({
        mutation: deleteRouteMutation,
        variables
      })
    },
    createRoute (variables) {
      return this.$apollo.mutate({
        mutation: createRouteMutation,
        variables
      })
    },
    updateRoute (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateRouteMutation,
        variables,
        context: {
          headers
        }
      })
    },
    deleteRoutePoint (variables) {
      return this.$apollo.mutate({
        mutation: deleteRoutePointMutation,
        variables
      })
    },
    createRoutePoint (variables) {
      return this.$apollo.mutate({
        mutation: createRoutePointMutation,
        variables
      })
    },
    updateRoutePoint (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateRoutePointMutation,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
